import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import { LocationField } from '@/components/common/custom-fields/location-filed';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetAvatar from '@/hooks/useGetAvatar';
import useGetAvatarProperties from '@/hooks/useGetAvatarProperties';
import { composeUri } from '@/utils/compose';
import {
    ADDRESS_TEXT,
    AVATAR_PROPERTIES,
    AVATARS,
    CITY,
    CONTACT_EMAIL_AVATAR_PROPERTY,
    CONTACT_NAME_AVATAR_PROPERTY,
    CONTACT_PHONE_AVATAR_PROPERTY,
    COUNTRY,
    POSTAL_CODE,
    ROLE_BIG_DEMO,
    SITES,
} from '@/utils/CONST';
import { getAvatarPropertyId, getAvatarPropertyValue } from '@/utils/getProperty';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { BooleanInput, Form, TextInput, useNotify, usePermissions, useTranslate, useUpdate } from 'react-admin';

let siteAvatarPropertiesNames = [
    CONTACT_NAME_AVATAR_PROPERTY,
    CONTACT_PHONE_AVATAR_PROPERTY,
    CONTACT_EMAIL_AVATAR_PROPERTY,
];

interface EditSiteDialogProps {
    open: boolean;
    onClose: () => void;
    siteId: string;
    clientId: string;
}

const EditSiteDialog = ({ open, onClose, siteId, clientId }: EditSiteDialogProps) => {
    const translate = useTranslate();
    const notify = useNotify();
    // const [dirty, setDirty] = useState(false);
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const { avatar, isLoading: isLoadingAvatar } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, siteId ?? ''),
        queryKey:`${SITES}_${clientId}`
    });
    const { properties, isLoading: isLoadingProperties } = useGetAvatarProperties({
        avatarId: composeUri(AVATARS, siteId),
        propertiesNames: siteAvatarPropertiesNames,
    });
    const { permissions } = usePermissions();

    const handleSubmit = ({
        name,
        [ADDRESS_TEXT]: addressText,
        [POSTAL_CODE]: postalCode,
        [COUNTRY]: country,
        [CITY]: city,
        latitude,
        longitude,
        demo,
        ...avatarProperties
    }: any) => {
        update(
            `${SITES}_${clientId}`,
            {
                id: avatar.id,
                data: {
                    name,
                    addressText: addressText,
                    postalCode: postalCode,
                    country: country,
                    city: city,
                    latitude: latitude.toString(),
                    longitude: longitude.toString(),
                    customFields: {
                        demo: !!demo,
                    },
                },
                meta: {
                    realResource: AVATARS,
                },
            },
            {
                onSuccess: () => {
                    let propertiesToUpdate: any[] = [];

                    siteAvatarPropertiesNames.forEach((propertyName) => {
                        if (getAvatarPropertyValue(properties, propertyName) !== avatarProperties[propertyName]) {
                            propertiesToUpdate.push(
                                update(
                                    AVATAR_PROPERTIES,
                                    {
                                        id: getAvatarPropertyId(properties, propertyName),
                                        data: {
                                            value: avatarProperties[propertyName],
                                        },
                                    },
                                    {
                                        returnPromise: true,
                                    },
                                ),
                            );
                        }
                    });

                    Promise.all(propertiesToUpdate).then(() => {
                        notify('ra.notification.updated', {
                            type: 'success',
                            messageArgs: {
                                smart_count: 1,
                            },
                        });
                        onClose();
                    });
                },
            },
        );
    };

    return (
        <DialogTemplate
            icon={<BorderColorIcon />}
            title={translate('site.edit_site_label', {
                siteName: avatar?.name,
            })}
            open={open}
            onClose={onClose}
        >
            <Form
                onSubmit={handleSubmit}
                defaultValues={{
                    name: avatar?.name,
                    [ADDRESS_TEXT]: avatar?.addressText,
                    [POSTAL_CODE]: avatar?.postalCode,
                    [CITY]: avatar?.city,
                    [COUNTRY]: avatar?.country,
                    location: `Lat: ${avatar?.latitude}, Lng: ${avatar?.longitude}`,
                    latitude: parseFloat(avatar?.latitude),
                    longitude: parseFloat(avatar?.longitude),
                    demo: avatar?.customFields?.demo ?? false,
                    [CONTACT_NAME_AVATAR_PROPERTY]: getAvatarPropertyValue(
                        properties,
                        CONTACT_NAME_AVATAR_PROPERTY,
                    ) as string,
                    [CONTACT_PHONE_AVATAR_PROPERTY]: getAvatarPropertyValue(
                        properties,
                        CONTACT_PHONE_AVATAR_PROPERTY,
                    ) as string,
                    [CONTACT_EMAIL_AVATAR_PROPERTY]: getAvatarPropertyValue(
                        properties,
                        CONTACT_EMAIL_AVATAR_PROPERTY,
                    ) as string,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput helperText={false} name={'name'} source={'name'} label={'site.form.site_name'} />

                        <LocationField readOnly={true} />

                        <TextInput
                            helperText={false}
                            name={ADDRESS_TEXT}
                            source={ADDRESS_TEXT}
                            label={'site.form.addressText'}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={POSTAL_CODE}
                            source={POSTAL_CODE}
                            label={'site.form.postalCode'}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={CITY}
                            source={CITY}
                            label={'site.form.city'}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={COUNTRY}
                            source={COUNTRY}
                            label={'site.form.country'}
                            readOnly={true}
                        />

                        <TextInput
                            helperText={false}
                            name={CONTACT_NAME_AVATAR_PROPERTY}
                            source={CONTACT_NAME_AVATAR_PROPERTY}
                            label={'site.form.name'}
                        />

                        <TextInput
                            helperText={false}
                            name={CONTACT_PHONE_AVATAR_PROPERTY}
                            source={CONTACT_PHONE_AVATAR_PROPERTY}
                            label={'site.form.phone'}
                        />

                        <TextInput
                            helperText={false}
                            name={CONTACT_EMAIL_AVATAR_PROPERTY}
                            source={CONTACT_EMAIL_AVATAR_PROPERTY}
                            label={'site.form.email'}
                        />

                        {permissions?.includes(ROLE_BIG_DEMO) && (
                            <BooleanInput
                                label='site.form.demo'
                                source='demo'
                                helperText={false}
                                sx={{
                                    ml: 1,
                                }}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={onClose} disabled={isLoadingUpdate}>
                            {translate('buttons.cancel')}
                        </Button>

                        <LoadingButton
                            type='submit'
                            variant='contained'
                            loading={isLoadingUpdate || isLoadingAvatar || isLoadingProperties}
                        >
                            {translate('buttons.validate')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default EditSiteDialog;
